/** Stable domain error codes returned in `error.code`. The web app maps these to human messages. */
export const ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
  MFA_REQUIRED: 'MFA_REQUIRED',
  SESSION_REVOKED: 'SESSION_REVOKED',
  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
  INVALID_RESET_TOKEN: 'INVALID_RESET_TOKEN',
  PASSWORD_POLICY_VIOLATION: 'PASSWORD_POLICY_VIOLATION',
  CSRF_CHECK_FAILED: 'CSRF_CHECK_FAILED',
  RATE_LIMITED: 'RATE_LIMITED',
  TENANT_ACCESS_DENIED: 'TENANT_ACCESS_DENIED',
  TENANT_INACTIVE: 'TENANT_INACTIVE',
  TENANT_CODE_TAKEN: 'TENANT_CODE_TAKEN',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  SENSITIVE_FIELD_ACCESS_DENIED: 'SENSITIVE_FIELD_ACCESS_DENIED',
  GRANT_EXCEEDS_AUTHORITY: 'GRANT_EXCEEDS_AUTHORITY',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  INVALID_STATE_TRANSITION: 'INVALID_STATE_TRANSITION',
  REASON_REQUIRED: 'REASON_REQUIRED',
  CONFIGURATION_INVALID: 'CONFIGURATION_INVALID',
  INVITATION_INVALID: 'INVITATION_INVALID',
  EMAIL_ALREADY_MEMBER: 'EMAIL_ALREADY_MEMBER',
  LAST_OWNER_PROTECTED: 'LAST_OWNER_PROTECTED',
  DUPLICATE_CODE: 'DUPLICATE_CODE',
  DEPARTMENT_INACTIVE: 'DEPARTMENT_INACTIVE',
  DEPARTMENT_HAS_ACTIVE_EMPLOYEES: 'DEPARTMENT_HAS_ACTIVE_EMPLOYEES',
  TEAM_INACTIVE: 'TEAM_INACTIVE',
  LOCATION_HAS_ACTIVE_EMPLOYEES: 'LOCATION_HAS_ACTIVE_EMPLOYEES',
  LOCATION_INACTIVE: 'LOCATION_INACTIVE',
  LOCATION_CODE_IMMUTABLE: 'LOCATION_CODE_IMMUTABLE',
  INVALID_STATE_OR_UT: 'INVALID_STATE_OR_UT',
  INVALID_POSTAL_CODE: 'INVALID_POSTAL_CODE',
  EMPLOYEE_NOT_FOUND: 'EMPLOYEE_NOT_FOUND',
  EMPLOYEE_ARCHIVED: 'EMPLOYEE_ARCHIVED',
  DUPLICATE_EMPLOYEE_NUMBER: 'DUPLICATE_EMPLOYEE_NUMBER',
  EMPLOYEE_NUMBER_REQUIRED: 'EMPLOYEE_NUMBER_REQUIRED',
  EMPLOYEE_NUMBER_IMMUTABLE: 'EMPLOYEE_NUMBER_IMMUTABLE',
  DUPLICATE_WORK_EMAIL: 'DUPLICATE_WORK_EMAIL',
  INVALID_MANAGER: 'INVALID_MANAGER',
  REPORTING_CYCLE_DETECTED: 'REPORTING_CYCLE_DETECTED',
  CUSTOM_FIELD_INVALID: 'CUSTOM_FIELD_INVALID',
  EXIT_BLOCKED_OUTSTANDING_ASSETS: 'EXIT_BLOCKED_OUTSTANDING_ASSETS',
  FIELD_NOT_SELF_EDITABLE: 'FIELD_NOT_SELF_EDITABLE',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  FILE_TYPE_NOT_ALLOWED: 'FILE_TYPE_NOT_ALLOWED',
  DOCUMENT_NOT_FOUND: 'DOCUMENT_NOT_FOUND',
  IMPORT_VALIDATION_FAILED: 'IMPORT_VALIDATION_FAILED',
  IMPORT_ROW_LIMIT_EXCEEDED: 'IMPORT_ROW_LIMIT_EXCEEDED',
  IMPORT_ALREADY_COMMITTED: 'IMPORT_ALREADY_COMMITTED',
  TEMPLATE_NOT_PUBLISHED: 'TEMPLATE_NOT_PUBLISHED',
  TEMPLATE_VERSION_LOCKED: 'TEMPLATE_VERSION_LOCKED',
  ONBOARDING_CASE_EXISTS: 'ONBOARDING_CASE_EXISTS',
  TASK_EVIDENCE_REQUIRED: 'TASK_EVIDENCE_REQUIRED',
  LEAVE_POLICY_NOT_CONFIGURED: 'LEAVE_POLICY_NOT_CONFIGURED',
  LEAVE_NOT_ELIGIBLE: 'LEAVE_NOT_ELIGIBLE',
  INSUFFICIENT_LEAVE_BALANCE: 'INSUFFICIENT_LEAVE_BALANCE',
  LEAVE_OVERLAP: 'LEAVE_OVERLAP',
  LEAVE_EVIDENCE_REQUIRED: 'LEAVE_EVIDENCE_REQUIRED',
  LEAVE_NOTICE_PERIOD: 'LEAVE_NOTICE_PERIOD',
  LEAVE_INVALID_DATES: 'LEAVE_INVALID_DATES',
  LEAVE_APPROVER_NOT_RESOLVED: 'LEAVE_APPROVER_NOT_RESOLVED',
  LEAVE_SELF_APPROVAL: 'LEAVE_SELF_APPROVAL',
  LEAVE_NOT_CANCELLABLE: 'LEAVE_NOT_CANCELLABLE',
  WORK_CALENDAR_NOT_CONFIGURED: 'WORK_CALENDAR_NOT_CONFIGURED',
  ASSET_ALREADY_ASSIGNED: 'ASSET_ALREADY_ASSIGNED',
  ASSET_NOT_AVAILABLE: 'ASSET_NOT_AVAILABLE',
  ASSET_NOT_ASSIGNED: 'ASSET_NOT_ASSIGNED',
  DUPLICATE_SERIAL_NUMBER: 'DUPLICATE_SERIAL_NUMBER',
  DUPLICATE_ASSET_CODE: 'DUPLICATE_ASSET_CODE',
  UNAUTHORIZED_ASSET_AUDIT: 'UNAUTHORIZED_ASSET_AUDIT',
  TAG_ALREADY_APPLIED: 'TAG_ALREADY_APPLIED',
  TAG_NOT_APPLIED: 'TAG_NOT_APPLIED',
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Default human-readable messages (the API usually sends a more specific message). */
export const ERROR_MESSAGES: Partial<Record<ErrorCode, string>> = {
  INVALID_CREDENTIALS: 'The email or password is incorrect.',
  ACCOUNT_LOCKED: 'Too many failed attempts. The account is temporarily locked — try again later.',
  ACCOUNT_DISABLED: 'This account has been disabled. Contact your administrator.',
  SESSION_REVOKED: 'Your session has ended. Please sign in again.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  TENANT_ACCESS_DENIED: 'You do not have access to this organization.',
  LOCATION_HAS_ACTIVE_EMPLOYEES: 'This location still has active employees. Reassign them before deactivating.',
  LOCATION_INACTIVE: 'The selected location is inactive and cannot receive new assignments.',
  LEAVE_POLICY_NOT_CONFIGURED: 'No leave policy is configured for this leave type. Ask HR to configure one.',
  INSUFFICIENT_LEAVE_BALANCE: 'You do not have enough leave balance for this request.',
  LEAVE_OVERLAP: 'This request overlaps another leave request.',
  ASSET_ALREADY_ASSIGNED: 'This asset already has an active assignment.',
  ASSET_NOT_AVAILABLE: 'This asset is not available for assignment in its current status.',
  REPORTING_CYCLE_DETECTED: 'This manager change would create a reporting cycle.',
  RATE_LIMITED: 'Too many requests. Please slow down and try again shortly.',
};
